import { API, SequenceConfigScope } from "@swivl-mono/api-helper";
import { findBotSequenceConfigErrors } from "@swivl-mono/helpers";
import {
  BotSequenceConfigEffective,
  BotSequenceConfigInterface,
  BotSequenceConfigStep,
  BotSequenceConfigValues,
  BotSequenceMode,
  BotSequenceType
} from "@swivl-mono/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { extractErrorReason } from "../../../../Helpers/API/extractErrorReason";
import { SequenceScopeKind } from "../sequenceScope";

/** One field's staged edit: untouched, set to a value, or reverted to inherit (`null` in the PUT). */
export type FieldState<T> = { kind: "untouched" } | { kind: "set"; value: T } | { kind: "revert" };

export interface TypeDraft {
  mode: FieldState<BotSequenceMode>;
  ladder: FieldState<BotSequenceConfigStep[]>;
  minGapDays: FieldState<number>;
  pastDueInactiveOptInStatuses: FieldState<string[]>;
  /** Which schedule group supplies the send hours (decision 50; default "sequence"). */
  scheduleSlug: FieldState<string>;
}

export type TypeDraftField = keyof TypeDraft;

type DraftValue<K extends TypeDraftField> = TypeDraft[K] extends FieldState<infer T> ? T : never;

const UNTOUCHED: FieldState<never> = { kind: "untouched" };

export function emptyDraft(): TypeDraft {
  return { mode: UNTOUCHED, ladder: UNTOUCHED, minGapDays: UNTOUCHED, pastDueInactiveOptInStatuses: UNTOUCHED, scheduleSlug: UNTOUCHED };
}

/** The value the page shows for a field: the staged edit when there is one, else the effective value. */
export function shownValue<T>(state: FieldState<T>, effective: T, inherited: T): T {
  if (state.kind === "set") return state.value;
  if (state.kind === "revert") return inherited;
  return effective;
}

export interface UseSequenceConfigInput {
  scope: SequenceConfigScope;
  scopeKind: SequenceScopeKind;
}

export interface UseSequenceConfigResult {
  loading: boolean;
  error: string | null;
  saving: BotSequenceType | null;
  effective: Partial<Record<BotSequenceType, BotSequenceConfigEffective>>;
  /** The raw row stored at EXACTLY this scope for the type, if any. */
  ownRow(type: BotSequenceType): BotSequenceConfigInterface | undefined;
  draft(type: BotSequenceType): TypeDraft;
  dirtyCount(type: BotSequenceType): number;
  setField<K extends TypeDraftField>(type: BotSequenceType, field: K, value: DraftValue<K>): void;
  revertField(type: BotSequenceType, field: TypeDraftField): void;
  discardField(type: BotSequenceType, field: TypeDraftField): void;
  /** Validator messages for the staged draft (same messages the server would return). */
  validationErrors(type: BotSequenceType): string[];
  save(type: BotSequenceType): Promise<void>;
  cancel(type: BotSequenceType): void;
  /** Delete this scope's row for the type — everything reverts to inherited. */
  revertRow(type: BotSequenceType): Promise<void>;
  refresh(): Promise<void>;
}

function payloadForDraft(draft: TypeDraft): BotSequenceConfigValues {
  const payload: BotSequenceConfigValues = {};
  const fields: TypeDraftField[] = ["mode", "ladder", "minGapDays", "pastDueInactiveOptInStatuses", "scheduleSlug"];
  for (const field of fields) {
    const state = draft[field];
    if (state.kind === "set") {
      (payload as Record<string, unknown>)[field] = state.value;
    } else if (state.kind === "revert") {
      (payload as Record<string, unknown>)[field] = null;
    }
  }
  return payload;
}

function rowMatchesScope(row: BotSequenceConfigInterface, scope: SequenceConfigScope): boolean {
  if (scope.locationId != null) return row.locationId === scope.locationId;
  if (scope.groupId != null) return row.groupId === scope.groupId && row.locationId === null;
  return row.groupId === null && row.locationId === null;
}

/**
 * The Sequences tab's data layer (ENG-3216): the scope's effective config per type (with
 * per-field provenance), the raw rows (to find this scope's own row for delete), and a
 * staged draft per type that saves as ONE partial PUT — `set` → value, `revert` → `null`,
 * untouched fields omitted. Drafts are keyed by type so switching types keeps them, and
 * cleared whenever the scope changes.
 */
export function useSequenceConfig({ scope, scopeKind }: UseSequenceConfigInput): UseSequenceConfigResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<BotSequenceType | null>(null);
  const [effectiveList, setEffectiveList] = useState<BotSequenceConfigEffective[]>([]);
  const [rows, setRows] = useState<BotSequenceConfigInterface[]>([]);
  const [drafts, setDrafts] = useState<Partial<Record<BotSequenceType, TypeDraft>>>({});

  const scopeKey = `${scope.orgId}/${scope.groupId ?? "_"}/${scope.locationId ?? "_"}`;

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [effective, allRows] = await Promise.all([API.SequenceConfig.getEffectiveConfigs(scope), API.SequenceConfig.getConfigs(scope.orgId)]);
      setEffectiveList(effective);
      setRows(allRows);
    } catch (err) {
      setError(extractErrorReason(err));
    } finally {
      setLoading(false);
    }
    // scopeKey captures every scope field the request depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => {
    setDrafts({});
    void refresh();
  }, [refresh]);

  const effective = useMemo(() => {
    const byType: Partial<Record<BotSequenceType, BotSequenceConfigEffective>> = {};
    for (const entry of effectiveList) byType[entry.sequenceType] = entry;
    return byType;
  }, [effectiveList]);

  const ownRow = useCallback(
    (type: BotSequenceType): BotSequenceConfigInterface | undefined => rows.find((row) => row.sequenceType === type && rowMatchesScope(row, scope)),
    [rows, scope]
  );

  const draft = useCallback((type: BotSequenceType): TypeDraft => drafts[type] ?? emptyDraft(), [drafts]);

  const updateDraft = useCallback((type: BotSequenceType, update: (current: TypeDraft) => TypeDraft): void => {
    setDrafts((current) => ({ ...current, [type]: update(current[type] ?? emptyDraft()) }));
  }, []);

  const setField = useCallback(
    <K extends TypeDraftField>(type: BotSequenceType, field: K, value: DraftValue<K>): void => {
      updateDraft(type, (current) => ({ ...current, [field]: { kind: "set", value } }));
    },
    [updateDraft]
  );

  const revertField = useCallback(
    (type: BotSequenceType, field: TypeDraftField): void => {
      updateDraft(type, (current) => ({ ...current, [field]: { kind: "revert" } }));
    },
    [updateDraft]
  );

  const discardField = useCallback(
    (type: BotSequenceType, field: TypeDraftField): void => {
      updateDraft(type, (current) => ({ ...current, [field]: UNTOUCHED }));
    },
    [updateDraft]
  );

  const dirtyCount = useCallback(
    (type: BotSequenceType): number => Object.values(draft(type)).filter((state: FieldState<unknown>) => state.kind !== "untouched").length,
    [draft]
  );

  const validationErrors = useCallback((type: BotSequenceType): string[] => findBotSequenceConfigErrors(payloadForDraft(draft(type)), type), [draft]);

  const save = useCallback(
    async (type: BotSequenceType): Promise<void> => {
      const payload = payloadForDraft(draft(type));
      if (Object.keys(payload).length === 0) return;
      setSaving(type);
      setError(null);
      try {
        await API.SequenceConfig.updateConfig(scope, type, payload);
        await refresh();
        setDrafts((current) => ({ ...current, [type]: emptyDraft() }));
      } catch (err) {
        setError(extractErrorReason(err));
        throw err;
      } finally {
        setSaving(null);
      }
    },
    [draft, refresh, scope]
  );

  const cancel = useCallback((type: BotSequenceType): void => {
    setDrafts((current) => ({ ...current, [type]: emptyDraft() }));
  }, []);

  const revertRow = useCallback(
    async (type: BotSequenceType): Promise<void> => {
      const row = ownRow(type);
      if (!row) return;
      setSaving(type);
      setError(null);
      try {
        await API.SequenceConfig.deleteConfig(scope.orgId, row.id);
        await refresh();
        setDrafts((current) => ({ ...current, [type]: emptyDraft() }));
      } catch (err) {
        setError(extractErrorReason(err));
        throw err;
      } finally {
        setSaving(null);
      }
    },
    [ownRow, refresh, scope.orgId]
  );

  return {
    loading,
    error,
    saving,
    effective,
    ownRow,
    draft,
    dirtyCount,
    setField,
    revertField,
    discardField,
    validationErrors,
    save,
    cancel,
    revertRow,
    refresh
  };
}

/** Handy for callers that only care whether a scope is the org (no parent to revert to). */
export function hasParentScope(scopeKind: SequenceScopeKind): boolean {
  return scopeKind !== "org";
}
